import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { siteInfo, serviceOptions } = useSiteConfig();
  
  // Set page-specific SEO
  usePageSEO({
    title: "Contact Us - Request a Quote",
    description: "Request a quote for professional polygraph examination services. Contact The Lie Detective for employment screening, corporate investigations, or private matters across Africa.",
    path: "/contact"
  });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    service: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
    } catch (err) {
      // Fallback to localStorage if API fails
      const submissions = JSON.parse(localStorage.getItem("contact_submissions") || "[]");
      submissions.push({ ...formData, timestamp: new Date().toISOString() });
      localStorage.setItem("contact_submissions", JSON.stringify(submissions));
      setSubmitted(true);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 min-h-[80vh] flex items-center">
          <div className="absolute inset-0 bg-[#0A0A0F]" />
          <div className="relative z-10 max-w-2xl mx-auto px-6 lg:px-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#DC2626]/[0.1] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-[#DC2626]" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Thank You
            </h1>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Your enquiry has been received. We'll be in touch within 24 hours
              to discuss your requirements. All communications are strictly
              confidential.
            </p>
            <Link to="/">
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 font-medium transition-colors duration-200">
                Return Home
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            Get In Touch
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Request a
            <br />
            <span className="text-[#DC2626]">Confidential Quote</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            Complete the form below and we'll respond within 24 hours. All
            enquiries are treated with the strictest confidentiality.
          </p>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="name"
                      className="text-gray-300 text-sm font-medium"
                    >
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Your full name"
                      className="bg-[#111118] border-white/[0.06] text-white placeholder:text-gray-600 h-12 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-gray-300 text-sm font-medium"
                    >
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="your@email.com"
                      className="bg-[#111118] border-white/[0.06] text-white placeholder:text-gray-600 h-12 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="phone"
                      className="text-gray-300 text-sm font-medium"
                    >
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="+27 XX XXX XXXX"
                      className="bg-[#111118] border-white/[0.06] text-white placeholder:text-gray-600 h-12 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm font-medium">
                      Service Required *
                    </Label>
                    <Select
                      required
                      onValueChange={(val) => handleChange("service", val)}
                    >
                      <SelectTrigger className="bg-[#111118] border-white/[0.06] text-white h-12 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20 [&>span]:text-gray-600 data-[state=open]:border-[#DC2626]/50">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111118] border-white/[0.08]">
                        {serviceOptions.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            className="text-gray-300 focus:bg-[#DC2626]/10 focus:text-white cursor-pointer"
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="location"
                    className="text-gray-300 text-sm font-medium"
                  >
                    Your Location / City / Country *
                  </Label>
                  <Input
                    id="location"
                    required
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="e.g. Johannesburg, South Africa"
                    className="bg-[#111118] border-white/[0.06] text-white placeholder:text-gray-600 h-12 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20"
                  />
                  <p className="text-gray-600 text-xs">We offer services across Africa. Your location helps us prepare an accurate quote.</p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="message"
                    className="text-gray-300 text-sm font-medium"
                  >
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="Please describe your situation and requirements. All information is treated as strictly confidential."
                    rows={6}
                    className="bg-[#111118] border-white/[0.06] text-white placeholder:text-gray-600 focus:border-[#DC2626]/50 focus:ring-[#DC2626]/20 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium w-full md:w-auto transition-colors duration-200"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send Enquiry
                </Button>

                <p className="text-gray-600 text-xs">
                  By submitting this form, you agree that your information will
                  be used solely for the purpose of responding to your enquiry.
                  We comply with the Protection of Personal Information Act
                  (POPIA).
                </p>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-5">
              <div className="bg-[#111118] border border-white/[0.04] rounded-2xl p-8 lg:p-10 sticky top-28">
                <h3 className="text-white font-semibold text-xl mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                      <Phone className="h-5 w-5 text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">
                        Phone
                      </p>
                      <p className="text-white text-sm">{siteInfo.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                      <Mail className="h-5 w-5 text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">
                        Email
                      </p>
                      <p className="text-white text-sm">{siteInfo.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                      <MapPin className="h-5 w-5 text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">
                        Location
                      </p>
                      <p className="text-white text-sm">{siteInfo.location}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">
                        Office Hours
                      </p>
                      <p className="text-white text-sm">{siteInfo.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-white/[0.04]">
                  <p className="text-gray-500 text-sm leading-relaxed">
                    All enquiries are handled with strict confidentiality. We
                    typically respond within 24 hours during business days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
